import React, { useState, useRef, useEffect } from 'react';
import RecordRTC, { RecordRTCPromisesHandler } from 'recordrtc';
import { FiX, FiSend } from 'react-icons/fi';
import { pickRecorderMime } from '../utils/pickRecorderMime';
import './AudioRecorder.css';

interface AudioRecorderProps {
  onSend: (blob: Blob) => void;
  onCancel: () => void;
}

export const AudioRecorder: React.FC<AudioRecorderProps> = ({ onSend, onCancel }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<RecordRTCPromisesHandler | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    startRecording();
    return () => {
      stopAndCleanup();
    };
  }, []);

  const startRecording = async () => {
    try {
      // Primeiro, verificar se o navegador suporta a API de mídia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Your browser does not support audio recording');
      }

      // Tentar diferentes configurações de áudio
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true }
        }
      });

      streamRef.current = stream;

      // MediaStreamRecorder: evita StereoAudioRecorder (sempre WAV, rejeitado no WhatsApp Cloud/Spark).
      const picked = pickRecorderMime();

      recorderRef.current = new RecordRTCPromisesHandler(stream, {
        type: 'audio',
        // RecordRTC Options.mimeType is a narrow literal union; runtime pick is a string.
        mimeType: picked.mime as 'audio/ogg' | 'audio/webm',
        recorderType: RecordRTC.MediaStreamRecorder,
        numberOfAudioChannels: 1,
        timeSlice: 1000
      });

      await recorderRef.current.startRecording();
      setIsRecording(true);
      setRecordingTime(0);

      // Iniciar o timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err: any) {
      console.error('Error starting recording:', err);
      let errorMessage = 'Error starting recording';

      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'Microphone not found. Please connect a microphone and try again.';
      } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Microphone access denied. Please allow microphone access.';
      }

      setError(errorMessage);
      onCancel();
    }
  };

  const stopAndCleanup = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (recorderRef.current && isRecording) {
      try {
        await recorderRef.current.stopRecording();
        const blob = await recorderRef.current.getBlob();
        setAudioBlob(blob);
        setIsRecording(false);
      } catch (error) {
        console.error('Error stopping recording:', error);
      }
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const handleStopRecording = async () => {
    await stopAndCleanup();
  };

  const handleSend = async () => {
    if (audioBlob) {
      await stopAndCleanup();
      onSend(audioBlob);
      onCancel();
    }
  };

  const handleCancel = async () => {
    await stopAndCleanup();
    onCancel();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className="cweb-audio-rec-container cweb-audio-rec-error-wrap">
        <div className="cweb-audio-rec-error">
          <span>{error}</span>
          <button onClick={onCancel}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="cweb-audio-rec-container">
      <div className="cweb-audio-rec-wrap">
        <div className="cweb-audio-rec-status">
          <div className={`cweb-audio-rec-pulse ${isRecording ? 'cweb-audio-recording' : ''}`}></div>
          <span className="cweb-audio-rec-time">{formatTime(recordingTime)}</span>
        </div>
        <div className="cweb-audio-rec-actions">
          {isRecording ? (
            <button
              className="cweb-audio-rec-btn cweb-audio-rec-cancel"
              onClick={handleStopRecording}
              title="Stop recording"
            >
              <FiX size={20} />
            </button>
          ) : (
            <>
              <button
                className="cweb-audio-rec-btn cweb-audio-rec-cancel"
                onClick={handleCancel}
                title="Cancel"
              >
                <FiX size={20} />
              </button>
              {audioBlob && (
                <button
                  className="cweb-audio-rec-btn cweb-audio-rec-send"
                  onClick={handleSend}
                  title="Send recording"
                >
                  <FiSend size={20} />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
